"use client";

import { InferenceStatus, InferenceType } from "@/lib/playground-utils";
import { Capability } from "@/components/playground/prompt-types";
import { Card, CardContent } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import InferencePointing from "./inference-point";
import InferenceDetect from "./inference-detect";
import CodeExample from "./code-example";
import { useRef, useEffect, useState } from "react";
import { captionLengthFromString } from "./prompt-caption";
type PointData = { x: number; y: number }[];
type BoundingBoxData = {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
}[];
import CapabilityPill from "./capability-pill";

function renderCaptionResult(
  result: string | PointData | BoundingBoxData,
  status?: InferenceStatus,
) {
  if (status === "pending") {
    return (
      <span className="text-muted-foreground italic">Waiting…</span>
    );
  }

  if (status === "error") {
    const message =
      typeof result === "string" && result.length > 0
        ? result
        : "Unable to stream caption. Please try again.";
    return <span className="text-destructive">{message}</span>;
  }

  if (typeof result === "string") {
    const text = result.trimStart();
    return text.length > 0 ? text : "…";
  }

  return JSON.stringify(result);
}

function GroundingVisualization({ 
  image, 
  points 
}: { 
  image: string; 
  points: Array<[number, number]>; 
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scaledDimensions, setScaledDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    let isMounted = true;
    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;
      
      const MAX_DIMENSION = 400; // Slightly smaller for hover card
      let scaledWidth = imgElement.width;
      let scaledHeight = imgElement.height;

      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      canvas.width = scaledWidth * 2;
      canvas.height = scaledHeight * 2;

      // Let Tailwind handle responsive sizing, just set aspect ratio
      canvas.style.aspectRatio = `${scaledWidth} / ${scaledHeight}`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);

      if (
        scaledDimensions.width !== scaledWidth ||
        scaledDimensions.height !== scaledHeight
      ) {
        setScaledDimensions({ width: scaledWidth, height: scaledHeight });
      }

      ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);

      if (points.length > 0) {
        const pointColor = "rgb(40, 141, 255)";
        const transparentColor = "rgba(40, 141, 255, 0.3)";

        points.forEach((point) => {
          const x = point[0] * scaledWidth;
          const y = point[1] * scaledHeight;
          const outerRadius = 8; // Slightly smaller than pointing
          const middleRadius = 8;
          const innerRadius = 2;

          ctx.beginPath();
          ctx.arc(x, y, outerRadius, 0, 2 * Math.PI);
          ctx.strokeStyle = pointColor;
          ctx.lineWidth = 2;
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(x, y, middleRadius, 0, 2 * Math.PI);
          ctx.fillStyle = transparentColor;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(x, y, innerRadius, 0, 2 * Math.PI);
          ctx.fillStyle = pointColor;
          ctx.fill();
        });
      }
    };

    imgElement.src = image;

    return () => {
      isMounted = false;
    };
  }, [image, points, scaledDimensions.width, scaledDimensions.height]);

  return (
    <div className="p-2">
      <canvas
        ref={canvasRef}
        className="rounded-lg"
        style={{
          width: `${scaledDimensions.width}px`,
          height: `${scaledDimensions.height}px`,
        }}
      />
    </div>
  );
}

function renderReasoningText(
  reasoning: {
    text: string;
    grounding: Array<{
      start_idx: number;
      end_idx: number;
      points: Array<[number, number]>;
    }>;
  },
  image: string
) {
  if (!reasoning.grounding || reasoning.grounding.length === 0) {
    return reasoning.text;
  }

  // Sort grounding by start_idx to process in order
  const sortedGrounding = [...reasoning.grounding].sort((a, b) => a.start_idx - b.start_idx);
  
  let lastIndex = 0;
  const parts: React.ReactNode[] = [];
  
  sortedGrounding.forEach((ground, index) => {
    // Add text before this grounded span
    if (ground.start_idx > lastIndex) {
      parts.push(reasoning.text.slice(lastIndex, ground.start_idx));
    }
    
    // Add the grounded span with hover card
    parts.push(
      <HoverCard key={index}>
        <HoverCardTrigger asChild>
          <span className="border-b border-dotted border-primary cursor-pointer">
            {reasoning.text.slice(ground.start_idx, ground.end_idx)}
          </span>
        </HoverCardTrigger>
        <HoverCardContent className="w-auto p-0">
          <GroundingVisualization image={image} points={ground.points} />
        </HoverCardContent>
      </HoverCard>
    );
    
    lastIndex = ground.end_idx;
  });
  
  // Add remaining text after the last grounded span
  if (lastIndex < reasoning.text.length) {
    parts.push(reasoning.text.slice(lastIndex));
  }
  
  return <>{parts}</>;
}

function InferenceVisualQuery({
  image,
  result,
  query,
  reasoning,
  duration,
  imageFile,
}: {
  image: string;
  result: string | PointData | BoundingBoxData;
  query: string;
  reasoning?: {
    text: string;
    grounding: Array<{
      start_idx: number;
      end_idx: number;
      points: Array<[number, number]>;
    }>;
  };
  duration?: number;
  imageFile?: File;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scaledDimensions, setScaledDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    let isMounted = true;
    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;
      
      const MAX_DIMENSION = 512;
      let scaledWidth = imgElement.width;
      let scaledHeight = imgElement.height;

      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      canvas.width = scaledWidth * 2;
      canvas.height = scaledHeight * 2;

      // Let Tailwind handle responsive sizing, just set aspect ratio
      canvas.style.aspectRatio = `${scaledWidth} / ${scaledHeight}`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);

      if (
        scaledDimensions.width !== scaledWidth ||
        scaledDimensions.height !== scaledHeight
      ) {
        setScaledDimensions({ width: scaledWidth, height: scaledHeight });
      }

      ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);
    };

    imgElement.src = image;

    return () => {
      isMounted = false;
    };
  }, [image, scaledDimensions.width, scaledDimensions.height]);

  return (
    <Card>
      <CardContent className="mt-6">
        <div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="flex-shrink-0 w-full sm:w-auto">
              <canvas
                ref={canvasRef}
                className="rounded-lg w-full max-w-full sm:w-auto sm:max-w-md h-auto"
              />
            </div>

            <div className="flex flex-col gap-2 min-w-0 flex-grow">
              <div>
                <CapabilityPill capability={Capability.VisualQuery} />
              </div>
              <div>
                <div className="text-xs font-black text-secondary-foreground">
                  PROMPT
                </div>
                <div className="text-sm break-words">{query}</div>
              </div>
              {reasoning && (
                <div>
                  <div className="text-xs font-black text-secondary-foreground">
                    REASONING
                  </div>
                  <div className="text-sm whitespace-pre-wrap break-words">
                    {renderReasoningText(reasoning, image)}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs font-black text-secondary-foreground">
                  RESULT
                </div>
                <div className="text-sm whitespace-pre-wrap break-words">
                  {typeof result === "string" ? result.trimStart() : JSON.stringify(result)}
                </div>
              </div>
              {duration && (
                <div className="text-xs text-muted-foreground mt-2">
                  Response time: {duration.toFixed(0)}ms
                </div>
              )}
            </div>
          </div>
        </div>
        {imageFile && (
          <CodeExample
            capability={Capability.VisualQuery}
            prompt={query}
            imageFile={imageFile}
          />
        )}
      </CardContent>
    </Card>
  );
}

function InferenceCaption({
  image,
  result,
  captionLength,
  duration,
  imageFile,
  status,
}: {
  image: string;
  result: string | PointData | BoundingBoxData;
  captionLength: string;
  duration?: number;
  imageFile?: File;
  status?: InferenceStatus;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scaledDimensions, setScaledDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    let isMounted = true;
    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;
      
      const MAX_DIMENSION = 512;
      let scaledWidth = imgElement.width;
      let scaledHeight = imgElement.height;

      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      canvas.width = scaledWidth * 2;
      canvas.height = scaledHeight * 2;

      // Let Tailwind handle responsive sizing, just set aspect ratio
      canvas.style.aspectRatio = `${scaledWidth} / ${scaledHeight}`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);

      if (
        scaledDimensions.width !== scaledWidth ||
        scaledDimensions.height !== scaledHeight
      ) {
        setScaledDimensions({ width: scaledWidth, height: scaledHeight });
      }

      ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);
    };

    imgElement.src = image;

    return () => {
      isMounted = false;
    };
  }, [image, scaledDimensions.width, scaledDimensions.height]);

  return (
    <Card>
      <CardContent className="mt-6">
        <div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="flex-shrink-0 w-full sm:w-auto">
              <canvas
                ref={canvasRef}
                className="rounded-lg w-full max-w-full sm:w-auto sm:max-w-md h-auto"
              />
            </div>

            <div className="flex flex-col gap-2 min-w-0 flex-grow">
              <div>
                <CapabilityPill capability={Capability.Caption} />
              </div>
              <div>
                <div className="text-xs font-black text-muted-foreground">
                  LENGTH: {captionLength.toUpperCase()}
                </div>
              </div>
              <div>
                <div className="text-xs font-black text-muted-foreground">
                  RESULT
                </div>
                <div className="text-sm text-secondary-foreground whitespace-pre-wrap break-words">
                  {renderCaptionResult(result, status)}
                </div>
              </div>
              {status === "done" && duration && (
                <div className="text-xs text-muted-foreground mt-2">
                  Response time: {duration.toFixed(0)}ms
                </div>
              )}
            </div>
          </div>
        </div>
        {imageFile && (
          <CodeExample
            capability={Capability.Caption}
            prompt=""
            imageFile={imageFile}
            options={{ length: captionLengthFromString(captionLength) }}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default function Inference({
  image,
  result,
  capability,
  query,
  captionLength,
  reasoning,
  duration,
  imageFile,
  status,
}: InferenceType) {
  // Remove unused state
  // const [scaledDimensions] = useState<{
  //   width: number;
  //   height: number;
  // }>({ width: 0, height: 0 });

  switch (capability) {
    case Capability.VisualQuery:
      return (
        <InferenceVisualQuery 
          image={image} 
          result={result} 
          query={query} 
          reasoning={reasoning}
          duration={duration}
          imageFile={imageFile}
        />
      );
    case Capability.Caption:
      return (
        <InferenceCaption
          image={image}
          result={result}
          captionLength={captionLength as string}
          duration={duration}
          imageFile={imageFile}
          status={status}
        />
      );
    case Capability.Pointing:
      return (
        <InferencePointing
          image={image}
          result={result as PointData}
          query={query}
          duration={duration}
          imageFile={imageFile}
        />
      );
    case Capability.ObjectDetection:
      return (
        <InferenceDetect
          image={image}
          result={result as BoundingBoxData}
          query={query}
          duration={duration}
          imageFile={imageFile}
        />
      );
    default:
      throw new Error(`Unknown capability: ${capability}`);
  }
}

// export function InferenceOld({
//   image,
//   result,
//   capability,
//   query,
// }: InferenceType) {
//   // Check if the image is a data URL or a path
//   const isDataUrl = image.startsWith("data:");
//   const canvasRef = useRef<HTMLCanvasElement>(null);
//   const [scaledDimensions, setScaledDimensions] = useState<{
//     width: number;
//     height: number;
//   }>({ width: 0, height: 0 });
//   // Improved task type detection that works with empty arrays
//   const isObjectDetection =
//     capability === Capability.ObjectDetection ||
//     (Array.isArray(result) && result.length > 0 && "x_min" in result[0]);
//   const isPointingTask =
//     capability === Capability.Pointing ||
//     (Array.isArray(result) && result.length > 0 && "x" in result[0]);

//   // Helper function to format query message
//   const getQueryMessage = () => {
//     switch (capability) {
//       case Capability.VisualQuery:
//         return query;
//       case Capability.Caption:
//         let caption_type = query.charAt(0).toUpperCase() + query.slice(1);
//         if (caption_type == "Normal") caption_type = "Long";
//         return `${caption_type} caption:`;
//       case Capability.ObjectDetection:
//         return `Object detection: ${query || "all objects"}`;
//       case Capability.Pointing:
//         return `Point: ${query || "all objects"}`;
//       default:
//         return query;
//     }
//   };

//   // Draw detection results on canvas when component mounts or results change
//   useEffect(() => {
//     if (!canvasRef.current || (!isObjectDetection && !isPointingTask)) return;

//     // We've improved type handling in the component logic

//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext("2d");
//     if (!ctx) return;

//     // Track if the effect is still current
//     let isMounted = true;

//     // Create an image to draw
//     const imgElement = new window.Image();

//     // Set up onload before setting src to avoid race conditions
//     imgElement.onload = () => {
//       // Check if component is still mounted
//       if (!isMounted || !ctx) return;
//       // Calculate scaled dimensions (max 512px for largest dimension)
//       const MAX_DIMENSION = 512;
//       let scaledWidth = imgElement.width;
//       let scaledHeight = imgElement.height;

//       // Scale down if either dimension exceeds the max
//       if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
//         if (scaledWidth >= scaledHeight) {
//           // Width is the larger dimension
//           const aspectRatio = scaledHeight / scaledWidth;
//           scaledWidth = MAX_DIMENSION;
//           scaledHeight = MAX_DIMENSION * aspectRatio;
//         } else {
//           // Height is the larger dimension
//           const aspectRatio = scaledWidth / scaledHeight;
//           scaledHeight = MAX_DIMENSION;
//           scaledWidth = MAX_DIMENSION * aspectRatio;
//         }
//       }

//       // Set canvas dimensions to 2x scaled size for higher DPI rendering
//       canvas.width = scaledWidth * 2;
//       canvas.height = scaledHeight * 2;

//       // Clear the canvas first to avoid artifacts
//       ctx.clearRect(0, 0, canvas.width, canvas.height);

//       // Scale up all drawing operations by 2x to improve dpi
//       ctx.scale(2, 2);

//       // Only update dimensions if they've changed to prevent render loops
//       if (
//         scaledDimensions.width !== scaledWidth ||
//         scaledDimensions.height !== scaledHeight
//       ) {
//         setScaledDimensions({ width: scaledWidth, height: scaledHeight });
//       }

//       // Draw the image at the original scaled dimensions
//       ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);

//       if (isObjectDetection) {
//         const boxes = Array.isArray(result) ? (result as BoundingBoxData) : [];

//         // Only add dimming if we have boxes to show
//         if (boxes.length > 0) {
//           // Draw dimming overlay first (30% dim)
//           ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
//           ctx.fillRect(0, 0, scaledWidth, scaledHeight);

//           ctx.strokeStyle = "rgba(40, 141, 255, 0.9)";
//           ctx.lineWidth = 3;

//           // For each box, clear the area inside it and draw the stroke
//           boxes.forEach((box) => {
//             const x = box.x_min * scaledWidth;
//             const y = box.y_min * scaledHeight;
//             const width = (box.x_max - box.x_min) * scaledWidth;
//             const height = (box.y_max - box.y_min) * scaledHeight;

//             // Clear the dimming inside the box by drawing the original image section
//             ctx.save();
//             ctx.beginPath();
//             ctx.rect(x, y, width, height);
//             ctx.clip();
//             ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);
//             ctx.restore();

//             // Draw the blue border
//             ctx.strokeRect(x, y, width, height);
//           });
//         }
//       } else if (isPointingTask) {
//         const points = Array.isArray(result) ? (result as PointData) : [];

//         if (points.length > 0) {
//           const pointColor = "rgb(40, 141, 255)"; // Solid blue color
//           const transparentColor = "rgba(40, 141, 255, 0.3)"; // Semi-transparent blue

//           points.forEach((point) => {
//             const x = point.x * scaledWidth;
//             const y = point.y * scaledHeight;
//             const outerRadius = 12; // Outer ring radius
//             const middleRadius = 12; // Semi-transparent area radius
//             const innerRadius = 3; // Solid inner dot radius

//             // Draw outer ring with solid border
//             ctx.beginPath();
//             ctx.arc(x, y, outerRadius, 0, 2 * Math.PI);
//             ctx.strokeStyle = pointColor;
//             ctx.lineWidth = 2;
//             ctx.stroke();

//             // Draw middle area with semi-transparent fill
//             ctx.beginPath();
//             ctx.arc(x, y, middleRadius, 0, 2 * Math.PI);
//             ctx.fillStyle = transparentColor;
//             ctx.fill();

//             // Draw inner dot with solid fill
//             ctx.beginPath();
//             ctx.arc(x, y, innerRadius, 0, 2 * Math.PI);
//             ctx.fillStyle = pointColor;
//             ctx.fill();
//           });
//         }
//       }
//     };

//     // Set the image source after setting up onload
//     imgElement.src = image;

//     // Clean up function
//     return () => {
//       isMounted = false;
//     };
//   }, [
//     image,
//     result,
//     isObjectDetection,
//     isPointingTask,
//     scaledDimensions.width,
//     scaledDimensions.height,
//   ]);

//   // If it's a string result (like a caption), display it in the original format
//   if (!isObjectDetection && !isPointingTask) {
//     const displayResult =
//       typeof result === "string" ? result : JSON.stringify(result, null, 2);
//     const queryMessage = getQueryMessage();

//     return (
//       <div className="flex gap-4 p-4 bg-muted/20 rounded-xl">
//         <div className="flex-shrink-0 w-32 h-32 relative rounded-lg overflow-hidden">
//           {isDataUrl ? (
//             // Handle data URLs
//             <Image
//               src={image}
//               alt="Inference"
//               fill
//               style={{ objectFit: "cover" }}
//               className="rounded-lg"
//             />
//           ) : (
//             // Handle regular image paths
//             <Image
//               src={image}
//               alt="Inference"
//               width={128}
//               height={128}
//               className="rounded-lg object-cover"
//             />
//           )}
//         </div>
//         <div className="flex-grow flex flex-col gap-2">
//           {/* Message bubble for the query */}
//           {queryMessage && (
//             <div className="bg-blue-100 dark:bg-blue-600 text-blue-800 dark:text-white px-3 py-2 rounded-lg text-sm font-medium self-start mb-2 inline-block">
//               {queryMessage}
//             </div>
//           )}

//           {/* Result display */}
//           <div className="rounded-xl bg-white shadow-sm p-4 flex-1 dark:bg-muted/50 ">
//             <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
//               {displayResult}
//             </pre>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // For object detection and pointing tasks, display the visualization
//   const queryMessage = getQueryMessage();

//   return (
//     <Card>
//       <CardContent>
//         <div className="max-w-full">
//           {/* Message bubble for the query */}
//           {queryMessage && (
//             <div className="bg-blue-100 dark:bg-blue-600 text-blue-800 dark:text-white px-3 py-2 rounded-lg text-sm font-medium self-start mb-2 inline-block">
//               {queryMessage}
//             </div>
//           )}

//           <div className="overflow-auto">
//             <div className="relative rounded-lg shadow-sm p-2">
//               <canvas
//                 ref={canvasRef}
//                 className="rounded-lg max-w-full"
//                 style={{
//                   width: `${scaledDimensions.width}px`,
//                   height: `${scaledDimensions.height}px`,
//                 }}
//               />
//             </div>
//             {/* Only show a message if no results are detected */}
//             {Array.isArray(result) && result.length === 0 && (
//               <div className="mt-2 text-sm text-amber-500">
//                 {isObjectDetection
//                   ? "No objects detected in the image."
//                   : isPointingTask
//                   ? "No points identified in the image."
//                   : "No results found."}
//               </div>
//             )}
//           </div>
//         </div>
//       </CardContent>
//     </Card>
//   );
// }
